/**
 * De annuleringsvoorwaarde van een woning: kiezen, aanpassen of nieuw maken.
 *
 * Dit staat op het instellingenscherm en niet op een eigen pagina, omdat de vraag
 * die iemand hier heeft altijd één woning betreft: "wat krijgt mijn gast terug als
 * hij afzegt?" Een aparte pagina met een lijst voorwaarden beantwoordt die vraag
 * pas na twee klikken.
 *
 * Drie dingen die geen opmaak zijn:
 *
 * 1. **Geen voorwaarde is een zichtbare keuze, geen leeg veld.** Staat er niets,
 *    dan krijgt de gast bij annulering niets terug. Dat is een geldige afspraak,
 *    maar hij hoort met zoveel woorden op het scherm te staan — stilte leest een
 *    verhuurder als "dat regel ik later" en een gast als "dat komt wel goed".
 * 2. **Een voorwaarde die op meer woningen zit, waarschuwt.** Wie hem hier
 *    aanpast, past hem overal aan. Dat is de bedoeling van hergebruik, maar niet
 *    iets om per ongeluk te doen.
 * 3. **De staffels worden als geheel opgeslagen.** Er is geen knop die er één
 *    toevoegt en meteen bewaart; je bewerkt de hele voorwaarde en slaat hem op.
 */
import { useState } from 'react'
import type { ReactElement } from 'react'

import { useTranslate } from '../lib/i18n-provider'
import { POLICY_PRESETS, createPolicy, deletePolicy, savePolicy } from '../lib/cancellation-client'
import type {
  CancellationMode,
  CancellationPolicy,
  CancellationTier,
  PolicyBody,
  PolicyFailure,
} from '../lib/cancellation-client'

/** Promille naar een percentage voor het invoerveld: 500 wordt "50". */
function permilleToPercent(permille: number): string {
  return String(permille / 10)
}

function percentToPermille(value: string): number | null {
  const normalized = value.trim().replace(',', '.')
  if (normalized === '' || !/^\d+(\.\d)?$/.test(normalized)) {
    return null
  }
  const permille = Math.round(Number(normalized) * 10)
  return permille >= 0 && permille <= 1000 ? permille : null
}

/** De staffels in de vorm waarin ze worden getypt. */
interface TierDraft {
  days: string
  percent: string
}

interface Draft {
  name: string
  mode: CancellationMode
  flatPercent: string
  graceHours: string
  refundCleaningFee: boolean
  tiers: TierDraft[]
}

function toDraft(policy: CancellationPolicy | PolicyBody): Draft {
  return {
    name: policy.name,
    mode: policy.mode,
    flatPercent: permilleToPercent(policy.flatRefundPermille),
    graceHours: String(policy.graceHours),
    refundCleaningFee: policy.refundCleaningFee,
    tiers: policy.tiers.map((tier) => ({
      days: String(tier.daysBefore),
      percent: permilleToPercent(tier.refundPermille),
    })),
  }
}

/** Het concept naar een body, of de sleutel van wat er niet klopt. */
function toBody(draft: Draft): { body: PolicyBody } | { problem: string } {
  if (draft.name.trim() === '') {
    return { problem: 'dashboard.cancellation.problem.name' }
  }
  if (!/^\d+$/.test(draft.graceHours.trim())) {
    return { problem: 'dashboard.cancellation.problem.grace' }
  }

  // Bij een vast percentage hoeven de staffels niet te kloppen — ze doen niet mee.
  // Ze wél blijven controleren zou betekenen dat een half ingevulde rij die niemand
  // meer ziet, het opslaan tegenhoudt.
  if (draft.mode === 'flat') {
    const flat = percentToPermille(draft.flatPercent)
    if (flat === null) {
      return { problem: 'dashboard.cancellation.problem.flat' }
    }
    return {
      body: {
        name: draft.name.trim(),
        mode: 'flat',
        flatRefundPermille: flat,
        graceHours: Number(draft.graceHours.trim()),
        refundCleaningFee: draft.refundCleaningFee,
        tiers: [],
      },
    }
  }

  if (draft.tiers.length === 0) {
    // Een staffelvoorwaarde zonder staffels geeft nooit iets terug; dat is wat een
    // vast percentage van 0% uitdrukt, en daar is een eigen keuze voor.
    return { problem: 'dashboard.cancellation.problem.noTiers' }
  }

  const tiers: CancellationTier[] = []
  for (const tier of draft.tiers) {
    if (!/^\d+$/.test(tier.days.trim())) {
      return { problem: 'dashboard.cancellation.problem.days' }
    }
    const permille = percentToPermille(tier.percent)
    if (permille === null) {
      return { problem: 'dashboard.cancellation.problem.percent' }
    }
    tiers.push({ daysBefore: Number(tier.days.trim()), refundPermille: permille })
  }

  const sorted = [...tiers].sort((a, b) => b.daysBefore - a.daysBefore)
  const days = new Set<number>()
  for (const tier of sorted) {
    if (days.has(tier.daysBefore)) {
      return { problem: 'dashboard.cancellation.problem.duplicate' }
    }
    days.add(tier.daysBefore)
  }
  for (let index = 1; index < sorted.length; index += 1) {
    const earlier = sorted[index - 1]
    const later = sorted[index]
    // Eerder annuleren mag nooit minder opleveren; dat bedoelt niemand die het
    // intypt, en de API weigert het toch.
    if (
      earlier !== undefined &&
      later !== undefined &&
      later.refundPermille > earlier.refundPermille
    ) {
      return { problem: 'dashboard.cancellation.problem.increasing' }
    }
  }

  return {
    body: {
      name: draft.name.trim(),
      mode: 'staged',
      flatRefundPermille: percentToPermille(draft.flatPercent) ?? 0,
      graceHours: Number(draft.graceHours.trim()),
      refundCleaningFee: draft.refundCleaningFee,
      tiers: sorted,
    },
  }
}

export function CancellationSection({
  policies,
  selectedId,
  residenceId,
  busy,
  onSelect,
  onChanged,
  onFailure,
}: {
  readonly policies: readonly CancellationPolicy[]
  readonly selectedId: string | null
  readonly residenceId: string
  readonly busy: boolean
  readonly onSelect: (id: string | null) => void
  readonly onChanged: () => Promise<void>
  readonly onFailure: (error: PolicyFailure) => void
}): ReactElement {
  const t = useTranslate()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [working, setWorking] = useState(false)

  const selected = policies.find((policy) => policy.id === selectedId) ?? null
  const sharedWith =
    selected === null ? 0 : selected.residenceIds.filter((id) => id !== residenceId).length

  function startEditing(policy: CancellationPolicy): void {
    setEditingId(policy.id)
    setDraft(toDraft(policy))
    setProblem(null)
  }

  function startNew(preset: PolicyBody): void {
    setEditingId(null)
    setDraft(toDraft(preset))
    setProblem(null)
  }

  async function save(): Promise<void> {
    if (draft === null) {
      return
    }
    const outcome = toBody(draft)
    if ('problem' in outcome) {
      setProblem(outcome.problem)
      return
    }

    setWorking(true)
    const result =
      editingId === null
        ? await createPolicy(outcome.body)
        : await savePolicy(editingId, outcome.body)
    if (!result.ok) {
      onFailure(result.error)
      setWorking(false)
      return
    }

    // Een nieuwe voorwaarde wordt meteen de gekozene: hem aanmaken en dan nog een
    // keer moeten selecteren is een stap die niemand bedoelt.
    if (editingId === null) {
      onSelect(result.data.id)
    }
    setDraft(null)
    setEditingId(null)
    await onChanged()
    setWorking(false)
  }

  async function remove(policy: CancellationPolicy): Promise<void> {
    setWorking(true)
    const result = await deletePolicy(policy.id)
    if (!result.ok) {
      onFailure(result.error)
    } else if (policy.id === selectedId) {
      onSelect(null)
    }
    await onChanged()
    setWorking(false)
  }

  return (
    <section className="page__section">
      <h2 className="page__sectionTitle">{t('dashboard.cancellation.title')}</h2>
      <p className="prose">{t('dashboard.cancellation.intro')}</p>

      <label className="field">
        <span className="field__label">{t('dashboard.cancellation.field.policy')}</span>
        <select
          className="field__input"
          disabled={busy || working}
          onChange={(event) => {
            onSelect(event.target.value === '' ? null : event.target.value)
            setDraft(null)
          }}
          value={selectedId ?? ''}
        >
          {/* Geen voorwaarde is een keuze en geen leeg veld. */}
          <option value="">{t('dashboard.cancellation.none')}</option>
          {policies.map((policy) => (
            // De naam is door de verhuurder gegeven en dus data.
            <option key={policy.id} value={policy.id}>
              {policy.name}
            </option>
          ))}
        </select>
        <span className="field__hint">
          {selected === null
            ? t('dashboard.cancellation.noneHint')
            : t('dashboard.cancellation.selectedHint')}
        </span>
      </label>

      {selected !== null && draft === null && (
        <>
          <ul className="tier-list">
            {selected.graceHours > 0 && (
              <li>{t('dashboard.cancellation.summary.grace', { hours: selected.graceHours })}</li>
            )}
            {selected.mode === 'flat' ? (
              <li>
                {selected.flatRefundPermille === 0
                  ? t('dashboard.cancellation.summary.nothing')
                  : t('dashboard.cancellation.summary.flat', {
                      percent: selected.flatRefundPermille / 10,
                    })}
              </li>
            ) : (
              selected.tiers.map((tier) => (
                <li key={tier.daysBefore}>
                  {t('dashboard.cancellation.summary.tier', {
                    days: tier.daysBefore,
                    percent: tier.refundPermille / 10,
                  })}
                </li>
              ))
            )}
            {/* Altijd zichtbaar: het is een regel, geen instelling. */}
            <li>{t('dashboard.cancellation.summary.noShow')}</li>
            <li>
              {selected.refundCleaningFee
                ? t('dashboard.cancellation.summary.cleaningBack')
                : t('dashboard.cancellation.summary.cleaningKept')}
            </li>
          </ul>

          {sharedWith > 0 && (
            <p className="request-panel__hint">
              {t('dashboard.cancellation.shared', { count: sharedWith })}
            </p>
          )}

          <div className="request-panel__actions">
            <button
              className="button button--quiet"
              disabled={busy || working}
              onClick={() => {
                startEditing(selected)
              }}
              type="button"
            >
              {t('dashboard.cancellation.action.edit')}
            </button>
            <button
              className="button button--quiet"
              disabled={busy || working}
              onClick={() => void remove(selected)}
              type="button"
            >
              {t('dashboard.cancellation.action.remove')}
            </button>
          </div>
        </>
      )}

      {draft === null && (
        <div className="request-panel__actions">
          {Object.entries(POLICY_PRESETS).map(([key, preset]) => (
            <button
              className="button button--quiet"
              disabled={busy || working}
              key={key}
              onClick={() => {
                startNew(preset)
              }}
              type="button"
            >
              {t(`dashboard.cancellation.preset.${key}`)}
            </button>
          ))}
        </div>
      )}

      {draft !== null && (
        <div className="request-panel">
          <h3 className="request-panel__title">
            {editingId === null
              ? t('dashboard.cancellation.editor.new')
              : t('dashboard.cancellation.editor.edit')}
          </h3>

          <div className="field-grid">
            <label className="field">
              <span className="field__label">{t('dashboard.cancellation.field.name')}</span>
              <input
                className="field__input"
                disabled={working}
                maxLength={100}
                onChange={(event) => {
                  setDraft({ ...draft, name: event.target.value })
                }}
                type="text"
                value={draft.name}
              />
            </label>

            <label className="field">
              <span className="field__label">{t('dashboard.cancellation.field.grace')}</span>
              <input
                className="field__input"
                disabled={working}
                inputMode="numeric"
                onChange={(event) => {
                  setDraft({ ...draft, graceHours: event.target.value })
                }}
                type="text"
                value={draft.graceHours}
              />
              <span className="field__hint">{t('dashboard.cancellation.field.graceHint')}</span>
            </label>

            <label className="field">
              <span className="field__label">{t('dashboard.cancellation.field.mode')}</span>
              <select
                className="field__input"
                disabled={working}
                onChange={(event) => {
                  setDraft({ ...draft, mode: event.target.value as CancellationMode })
                }}
                value={draft.mode}
              >
                <option value="flat">{t('dashboard.cancellation.mode.flat')}</option>
                <option value="staged">{t('dashboard.cancellation.mode.staged')}</option>
              </select>
              <span className="field__hint">
                {t(`dashboard.cancellation.mode.${draft.mode}Hint`)}
              </span>
            </label>
          </div>

          {/* Een no-show is geen instelling maar een regel: wie niet komt en niet
              heeft geannuleerd, krijgt niets terug. Het staat er wel, want anders
              vraagt iedereen zich af waar het is gebleven. */}
          <p className="request-panel__hint">{t('dashboard.cancellation.noShowRule')}</p>

          <label className="field field--inline">
            <input
              checked={draft.refundCleaningFee}
              disabled={working}
              onChange={(event) => {
                setDraft({ ...draft, refundCleaningFee: event.target.checked })
              }}
              type="checkbox"
            />
            <span className="field__label">{t('dashboard.cancellation.field.cleaning')}</span>
          </label>

          {draft.mode === 'flat' && (
            <>
              <label className="field">
                <span className="field__label">{t('dashboard.cancellation.field.flat')}</span>
                <span className="field__group">
                  <input
                    className="field__input"
                    disabled={working}
                    inputMode="numeric"
                    onChange={(event) => {
                      setDraft({ ...draft, flatPercent: event.target.value })
                    }}
                    type="text"
                    value={draft.flatPercent}
                  />
                  <span className="field__suffix">%</span>
                </span>
                <span className="field__hint">{t('dashboard.cancellation.field.flatHint')}</span>
              </label>

              {/* De drie waarden die verhuurders in de praktijk kiezen, zonder ze op
                  te dringen: het veld hierboven neemt elk percentage. */}
              <div className="request-panel__actions">
                {['0', '50', '100'].map((percent) => (
                  <button
                    className="button button--quiet"
                    disabled={working}
                    key={percent}
                    onClick={() => {
                      setDraft({ ...draft, flatPercent: percent })
                    }}
                    type="button"
                  >
                    {t('dashboard.cancellation.action.setPercent', { percent: Number(percent) })}
                  </button>
                ))}
              </div>
            </>
          )}

          {draft.mode === 'staged' && (
            <>
              <h4 className="request-panel__title">{t('dashboard.cancellation.tiers.title')}</h4>
              <p className="request-panel__hint">{t('dashboard.cancellation.tiers.intro')}</p>
            </>
          )}

          {draft.mode === 'staged' &&
            draft.tiers.map((tier, index) => (
              // De index als sleutel: de rijen hebben geen eigen identiteit zolang ze
              // niet zijn opgeslagen, en de lijst wordt niet gesorteerd terwijl je
              // typt — sorteren gebeurt pas bij het opslaan.
              <div className="tier-row" key={`tier-${String(index)}`}>
                <label className="field">
                  <span className="field__label">{t('dashboard.cancellation.field.days')}</span>
                  <input
                    className="field__input"
                    disabled={working}
                    inputMode="numeric"
                    onChange={(event) => {
                      const next = [...draft.tiers]
                      next[index] = { ...tier, days: event.target.value }
                      setDraft({ ...draft, tiers: next })
                    }}
                    type="text"
                    value={tier.days}
                  />
                </label>
                <label className="field">
                  <span className="field__label">{t('dashboard.cancellation.field.percent')}</span>
                  <input
                    className="field__input"
                    disabled={working}
                    inputMode="numeric"
                    onChange={(event) => {
                      const next = [...draft.tiers]
                      next[index] = { ...tier, percent: event.target.value }
                      setDraft({ ...draft, tiers: next })
                    }}
                    type="text"
                    value={tier.percent}
                  />
                </label>
                <button
                  className="button button--quiet"
                  disabled={working}
                  onClick={() => {
                    setDraft({ ...draft, tiers: draft.tiers.filter((_, at) => at !== index) })
                  }}
                  type="button"
                >
                  {t('dashboard.cancellation.action.removeTier')}
                </button>
              </div>
            ))}

          {draft.mode === 'staged' && (
            <div className="request-panel__actions">
              <button
                className="button button--quiet"
                disabled={working}
                onClick={() => {
                  setDraft({ ...draft, tiers: [...draft.tiers, { days: '30', percent: '100' }] })
                }}
                type="button"
              >
                {t('dashboard.cancellation.action.addTier')}
              </button>
            </div>
          )}

          {problem !== null && (
            <p className="request-panel__problem" role="alert">
              {t(problem)}
            </p>
          )}

          <div className="request-panel__actions">
            <button className="button" disabled={working} onClick={() => void save()} type="button">
              {working
                ? t('dashboard.cancellation.action.saving')
                : t('dashboard.cancellation.action.save')}
            </button>
            <button
              className="button button--quiet"
              disabled={working}
              onClick={() => {
                setDraft(null)
                setProblem(null)
              }}
              type="button"
            >
              {t('dashboard.cancellation.action.cancel')}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
